import { CANONICAL_ROLES, FIELD_TYPES, MAX_PHONE_REGIONS, validateFormSchema, type FieldDefinition } from '@platform/form-rules';
import { z } from 'zod';

// The shape of a form's field list as it travels through APIs and site files (FR-5.4, FR-5.5).
// The Zod object gives editors and clients a typed shape; `validateFormSchema` — the same function
// ingest trusts — then checks everything that depends on more than one property.

const rulesSchema = z
  .object({
    minLength: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(0).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    pattern: z.string().max(200).optional(),
    options: z.array(z.string().min(1).max(200)).max(200).optional(),
    maxItems: z.number().int().min(0).optional(),
    minDate: z.string().optional(),
    maxDate: z.string().optional(),
    phoneRegions: z.array(z.string()).max(MAX_PHONE_REGIONS).optional(),
    phoneRegion: z.string().optional(),
  })
  .strict();

export const fieldDefinitionSchema = z
  .object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(200),
    type: z.enum(FIELD_TYPES),
    required: z.boolean().optional(),
    pii: z.boolean().optional(),
    role: z.enum(CANONICAL_ROLES).optional(),
    rules: rulesSchema.optional(),
  })
  .strict();

/** A complete, valid field list: at least one field, unique keys and roles, rules that fit their types. */
export const fieldsSchema = z
  .array(fieldDefinitionSchema)
  .max(200)
  .superRefine((fields, context) => {
    for (const issue of validateFormSchema({ fields })) {
      context.addIssue({ code: 'custom', message: `${issue.field}: ${issue.message}` });
    }
  })
  // `validateFormSchema` returning no issues is what makes a value a valid field list; Zod's
  // inferred type differs from FieldDefinition[] only in how optional properties are spelled.
  // cast: see above — validated by the refinement on the previous step.
  .transform((fields) => fields as FieldDefinition[]);
